import React, { memo } from 'react';

import { useToggle } from 'react-use';
import '../Filters/Filters.scss';

import { RangeField } from '../Filters/FilterFields/RangeField';
import { DateRangeField } from '../Filters/FilterFields/DateRangeField';

import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { formatMessageHelper } from '../../../shared/helpers/internationalization';
import { fieldTypeOptions, filterFields } from '../Filters/FilterFields/dataExplorerFieldsConfig';
import { FilterControls } from '../Filters/FilterFields/FilterControls';
import { InputField } from '../Filters/FilterFields/InputField';
import { RegionGroup } from '../Filters/FilterFields/RegionGroup';
import { DropdownField } from '../Filters/FilterFields/DropdownField';
import { CategoryField, useFormDataCategoryField } from '../Filters/FilterFields/CategoryField';
import { CategoriesModal } from '../Categories/CategoriesModal';
import { FiltersControl } from '../Filters/types';
import { getInputId } from '../Filters/FilterFields/getInputId';
import { CheckboxListField } from '../Filters/FilterFields/CheckboxListField';
import { themeFieldValueLabels, themeFieldValues } from '../Filters/auxiliaryFields';
import { useSelector } from 'react-redux';
import { getUserPermissionSet } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';

type Props = {
  control: FiltersControl;
  reset: () => void;
  isExtraFilterChanged?: boolean;
} & InjectedIntlProps;

const AllFiltersWithoutIntl = memo(({ intl, control, reset, isExtraFilterChanged }: Props) => {
  const [filtersOpen, setFiltersOpen] = useToggle(false);
  const [categoriesOpened, toggleCategoriesOpen] = useToggle(false);
  const { selectedCategory, onCategoryClick } = useFormDataCategoryField(control);
  const permissions = useSelector(getUserPermissionSet);
  const isThemesAvailable = permissions.has('CONSULTANT') || permissions.has('RESEARCHER');

  return (
    <div className="data-explorer-filters">
      <div className="data-explorer-filters__row">
        <div className="data-explorer-filters__search">
          <label htmlFor={getInputId(filterFields.search.name)} className="range-field__title">
            {formatMessageHelper(intl, 'dataExplorer.filters.search')}
          </label>
          <InputField
            controllerProps={{ name: filterFields.search.name, control }}
            inputProps={{
              type: 'search',
              className: 'filters__searchbar form__input form__input--large',
              placeholder: formatMessageHelper(intl, 'dataExplorer.filters.searchPlaceholder'),
            }}
          ></InputField>
        </div>
        <div>
          <CategoryField control={control} selectedCategory={selectedCategory} onOpenModal={toggleCategoriesOpen} />
        </div>

        {isThemesAvailable && (
          <CheckboxListField
            title={formatMessageHelper(intl, 'dataExplorer.filters.themeLabel')}
            control={control}
            name={'theme'}
            valuesList={themeFieldValues}
            labelsList={themeFieldValueLabels}
          />
        )}

        <RegionGroup control={control} />

        <DropdownField
          controllerProps={{ name: filterFields.type.name, control }}
          options={fieldTypeOptions}
          size="medium"
          title={formatMessageHelper(intl, 'dataExplorer.filters.typeLabel')}
        />

        <RangeField
          controllerProps={{ name: filterFields.alphaCount.name, control }}
          size="medium"
          title={formatMessageHelper(intl, 'dataExplorer.filters.alphasLabel')}
          min={filterFields.alphaCount.validations.min}
        />
        <DateRangeField
          controllerProps={{ name: filterFields.dateCreated.name, control }}
          title={formatMessageHelper(intl, 'dataExplorer.filters.dateAddedLabel')}
          placeholder={formatMessageHelper(intl, 'dataExplorer.filters.selectDatesPlaceholder')}
        />
        <FilterControls
          reset={reset}
          filtersOpen={filtersOpen}
          setFiltersOpen={setFiltersOpen}
          isExtraFieldsChanged={isExtraFilterChanged}
        />
      </div>
      <div>
        {categoriesOpened && (
          <CategoriesModal
            selectedCategory={selectedCategory}
            onCategoryClick={onCategoryClick}
            onClose={() => toggleCategoriesOpen(false)}
            control={control}
          />
        )}
      </div>
    </div>
  );
});

export const AllFieldFilters = injectIntl(AllFiltersWithoutIntl);
